import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { Loader2, Repeat } from "lucide-react";
import { resetPriceUpdateSentStatusPriceUpdateResetSentStatusPost } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";

export function ResetSentStatusDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const router = useRouter();

  const { mutateAsync: resetSentStatus, isPending: isResettingSentStatus } = useMutation({
    mutationFn: resetPriceUpdateSentStatusPriceUpdateResetSentStatusPost,
    onSuccess: (response) => {
      router.invalidate();
      toast({
        title: "Success",
        description: response.message,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive">
          <Repeat className="mr-2 h-4 w-4" />
          Reset Price Update Sent Status
        </Button>
      </DialogTrigger>
      <DialogContent className="my-4">
        <DialogHeader>
          <DialogTitle>Reset Price Update Sent Status</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-muted-foreground">
            This action will reset the sent status for today's price update, enabling the resending of price update
            emails.
          </div>

          <div className="rounded-md bg-amber-50 p-4">
            <div className="font-medium text-amber-800">Warning</div>
            <div className="mt-1 text-sm text-amber-700">
              Resetting the sent status and sending new price update emails will result in at least one client receiving
              duplicate price update emails. This feature should be used with caution.
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await resetSentStatus({ date: new Date().toISOString().split("T")[0] });
                setIsOpen(false);
              }}
              disabled={isResettingSentStatus}
            >
              {isResettingSentStatus ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Sent Status"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
